/**
 * Menu dropdown helpers — ported from ThemeMaker's
 * {@code lms-portal/src/components/shell/menuDropdown.ts}.
 *
 * <p>Centralised "does this menu item have a dropdown worth showing?"
 * Used by both the desktop header renderer and the mobile drawer so
 * the two stay in lockstep.
 *
 * <p>Mental model:
 * <ol>
 *   <li>Every link CAN have dropdown columns and/or a featured promo.</li>
 *   <li>Whether the dropdown renders is decided by data presence —
 *       not by an enable flag.</li>
 *   <li>Dropdown style ({@code 'mega'} vs {@code 'flat'}) is a
 *       separate orthogonal choice that only matters once we've
 *       decided to render.</li>
 * </ol>
 *
 * <p>This module exposes the presence checks. The visual rendering
 * lives in {@code MegaMenuLink} (for {@code 'mega'}) and a smaller
 * {@code FlatDropdownLink} helper (for {@code 'flat'}).
 */

import type { CurrentUser, MegaMenuConfig } from '../../portal';
import { isVisible, t } from '../../portal';

/** A column that, after permission filtering, has at least one
 *  visible link. */
export function hasNonEmptyColumns(
  megamenu: MegaMenuConfig | undefined,
  currentUser: CurrentUser | null,
): boolean {
  if (!megamenu?.columns?.length) return false;
  for (const col of megamenu.columns) {
    if (!isVisible(col, currentUser)) continue;
    if (!col.links?.length) continue;
    if (col.links.some((l) => isVisible(l, currentUser))) return true;
  }
  return false;
}

/** Featured promo "exists" when at least one of title or image is
 *  filled in (and the viewer is allowed to see it per
 *  {@code requireAuth} / {@code requirePerms}). Description and CTA
 *  on their own don't trigger rendering — the panel without a title
 *  or image would feel empty. */
export function hasFeaturedPromo(
  megamenu: MegaMenuConfig | undefined,
  currentUser: CurrentUser | null,
  locale: string,
  fallbackLocale: string,
): boolean {
  const f = megamenu?.featured;
  if (!f) return false;
  if (!isVisible(f, currentUser)) return false;
  const title = (t(f.title, locale, fallbackLocale) ?? '').trim();
  const hasImage = !!(f.image && f.image.trim());
  return !!title || hasImage;
}

/** Does this menu item have ANY dropdown content worth rendering? If
 *  false, the item should render as a plain link regardless of
 *  {@code dropdownStyle}. */
export function hasDropdownContent(
  megamenu: MegaMenuConfig | undefined,
  currentUser: CurrentUser | null,
  locale: string,
  fallbackLocale: string,
): boolean {
  return (
    hasNonEmptyColumns(megamenu, currentUser) ||
    hasFeaturedPromo(megamenu, currentUser, locale, fallbackLocale)
  );
}
